import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  Button,
  Paper,
  Grid,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tabs,
  Tab,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Refresh as RefreshIcon,
  Edit as EditIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  SwapHoriz as SwapHorizIcon,
  FilterList as FilterListIcon
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import {
  getPhysicalInventoryById,
  getPhysicalInventoryDiscrepancies,
  resolveDiscrepancy
} from '../../services/physicalInventoryService';
import { getLocations } from '../../services/locationService';
import AuthContext from '../../context/AuthContext';

const PhysicalInventoryReport = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [inventory, setInventory] = useState(null);
  const [discrepancies, setDiscrepancies] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [locations, setLocations] = useState([]);

  // Resolution dialog state
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedDiscrepancy, setSelectedDiscrepancy] = useState(null);
  const [resolutionType, setResolutionType] = useState('adjust');
  const [resolutionNotes, setResolutionNotes] = useState('');
  const [transferLocationId, setTransferLocationId] = useState('');
  const [resolving, setResolving] = useState(false);

  // Check if user is admin or purchaser
  const canResolveDiscrepancies = user && (user.role === 'admin' || user.role === 'purchaser');

  // Load inventory and discrepancies on initial render
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        // Get inventory details
        const inventoryData = await getPhysicalInventoryById(id);
        setInventory(inventoryData);

        // Get locations for this warehouse (for transfer option)
        const locationsData = await getLocations({ warehouse_id: inventoryData.warehouse_id });
        setLocations(locationsData);

        // Get discrepancies
        fetchDiscrepancies();

        setLoading(false);
      } catch (err) {
        console.error('Error fetching data:', err);
        setError('Failed to load inventory data. Please try again.');
        setLoading(false);
      }
    };

    fetchData();
  }, [id]);

  const fetchDiscrepancies = async () => {
    try {
      setRefreshing(true);

      // Get status filter based on tab
      const statusFilter = tabValue === 0 ? 'pending' :
                          tabValue === 1 ? 'adjusted' :
                          tabValue === 2 ? 'transferred' :
                          tabValue === 3 ? 'ignored' : '';

      const data = await getPhysicalInventoryDiscrepancies(id, {
        status: statusFilter,
        page: page + 1,
        limit: rowsPerPage
      });

      setDiscrepancies(data.items);
      setTotalItems(data.total);
      setRefreshing(false);
    } catch (err) {
      console.error('Error fetching discrepancies:', err);
      setError('Failed to load discrepancies. Please try again.');
      setRefreshing(false);
    }
  };

  useEffect(() => {
    if (inventory) {
      fetchDiscrepancies();
    }
  }, [page, rowsPerPage, tabValue]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    setPage(0);
  };

  const handleRefresh = () => {
    fetchDiscrepancies();
  };

  const handleOpenResolveDialog = (discrepancy) => {
    setSelectedDiscrepancy(discrepancy);
    setResolutionType('adjust');
    setResolutionNotes('');
    setTransferLocationId('');
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedDiscrepancy(null);
  };

  const handleResolveDiscrepancy = async () => {
    if (!resolutionNotes) {
      toast.error('Please enter resolution notes');
      return;
    }

    if (resolutionType === 'transfer' && !transferLocationId) {
      toast.error('Please select a transfer location');
      return;
    }

    try {
      setResolving(true);

      const resolutionData = {
        resolution: resolutionType,
        notes: resolutionNotes
      };

      if (resolutionType === 'transfer') {
        resolutionData.transfer_location_id = transferLocationId;
      }

      await resolveDiscrepancy(id, selectedDiscrepancy.id, resolutionData);

      toast.success('Discrepancy resolved successfully');
      setResolving(false);
      handleCloseDialog();

      // Refresh discrepancies
      fetchDiscrepancies();
    } catch (err) {
      console.error('Error resolving discrepancy:', err);
      toast.error('Failed to resolve discrepancy. Please try again.');
      setResolving(false);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Inventory Discrepancy Report
        </Typography>
        <Box>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={handleRefresh}
            disabled={refreshing}
            sx={{ mr: 1 }}
          >
            Refresh
          </Button>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/inventory/physical/list')}
          >
            Back to Inventory Counts
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 3, mb: 3 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Typography variant="h6" gutterBottom>
              Inventory Details
            </Typography>
            <Typography variant="body1">
              <strong>Warehouse:</strong> {inventory?.warehouse?.name}
            </Typography>
            <Typography variant="body1">
              <strong>Status:</strong> {' '}
              <Chip
                label={inventory?.status === 'in_progress' ? 'In Progress' : 'Completed'}
                color={inventory?.status === 'in_progress' ? 'primary' : 'success'}
                size="small"
              />
            </Typography>
            <Typography variant="body1">
              <strong>Started:</strong> {inventory?.started_at && new Date(inventory.started_at).toLocaleString()}
            </Typography>
            {inventory?.completed_at && (
              <Typography variant="body1">
                <strong>Completed:</strong> {new Date(inventory.completed_at).toLocaleString()}
              </Typography>
            )}
          </Grid>

          <Grid item xs={12} md={6}>
            <Typography variant="h6" gutterBottom>
              Discrepancy Summary
            </Typography>
            <Typography variant="body1">
              <strong>Total Discrepancies:</strong> {totalItems}
            </Typography>
            {!canResolveDiscrepancies && (
              <Alert severity="info" sx={{ mt: 2 }}>
                Only purchasers and administrators can resolve discrepancies.
              </Alert>
            )}
          </Grid>
        </Grid>
      </Paper>

      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          indicatorColor="primary"
          textColor="primary"
          variant="fullWidth"
        >
          <Tab label="Pending" />
          <Tab label="Adjusted" />
          <Tab label="Transferred" />
          <Tab label="Ignored" />
        </Tabs>

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Part Number</TableCell>
                <TableCell>Description</TableCell>
                <TableCell>Location</TableCell>
                <TableCell align="right">System Qty</TableCell>
                <TableCell align="right">Counted Qty</TableCell>
                <TableCell align="right">Discrepancy</TableCell>
                {tabValue !== 0 && <TableCell>Resolution</TableCell>}
                {canResolveDiscrepancies && tabValue === 0 && <TableCell align="center">Actions</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {discrepancies.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={canResolveDiscrepancies ? 7 : 6} align="center">
                    No discrepancies found
                  </TableCell>
                </TableRow>
              ) : (
                discrepancies.map((discrepancy) => (
                  <TableRow key={discrepancy.id}>
                    <TableCell>{discrepancy.part.internal_part_number}</TableCell>
                    <TableCell>{discrepancy.part.description}</TableCell>
                    <TableCell>
                      {`${discrepancy.location.warehouse.name} - ${discrepancy.location.rack}-${discrepancy.location.shelf}`}
                    </TableCell>
                    <TableCell align="right">{discrepancy.system_quantity}</TableCell>
                    <TableCell align="right">{discrepancy.counted_quantity}</TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        color: discrepancy.discrepancy > 0 ? 'success.main' :
                               discrepancy.discrepancy < 0 ? 'error.main' : 'text.primary',
                        fontWeight: 'bold'
                      }}
                    >
                      {discrepancy.discrepancy > 0 ? `+${discrepancy.discrepancy}` : discrepancy.discrepancy}
                    </TableCell>
                    {tabValue !== 0 && (
                      <TableCell>
                        <Box>
                          <Typography variant="body2">
                            {discrepancy.resolution_notes}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            By: {discrepancy.resolver?.username} on {new Date(discrepancy.resolved_at).toLocaleString()}
                          </Typography>
                        </Box>
                      </TableCell>
                    )}
                    {canResolveDiscrepancies && tabValue === 0 && (
                      <TableCell align="center">
                        <Tooltip title="Resolve Discrepancy">
                          <IconButton
                            color="primary"
                            onClick={() => handleOpenResolveDialog(discrepancy)}
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    )}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={totalItems}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>

      {/* Resolution Dialog */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Resolve Inventory Discrepancy</DialogTitle>
        <DialogContent>
          {selectedDiscrepancy && (
            <>
              <Grid container spacing={2} sx={{ mb: 2, mt: 1 }}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2">
                    <strong>Part:</strong> {selectedDiscrepancy.part.internal_part_number}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Description:</strong> {selectedDiscrepancy.part.description}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2">
                    <strong>Location:</strong> {`${selectedDiscrepancy.location.rack}-${selectedDiscrepancy.location.shelf}`}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Discrepancy:</strong> {' '}
                    <span style={{
                      color: selectedDiscrepancy.discrepancy > 0 ? 'green' :
                             selectedDiscrepancy.discrepancy < 0 ? 'red' : 'inherit',
                      fontWeight: 'bold'
                    }}>
                      {selectedDiscrepancy.discrepancy > 0 ? `+${selectedDiscrepancy.discrepancy}` : selectedDiscrepancy.discrepancy}
                    </span>
                  </Typography>
                </Grid>
              </Grid>

              <DialogContentText sx={{ mb: 2 }}>
                Choose how to resolve this discrepancy:
              </DialogContentText>

              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Resolution Type</InputLabel>
                <Select
                  value={resolutionType}
                  onChange={(e) => setResolutionType(e.target.value)}
                  label="Resolution Type"
                >
                  <MenuItem value="adjust">
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <CheckCircleIcon sx={{ mr: 1, color: 'success.main' }} />
                      Adjust Inventory (Update quantity to match count)
                    </Box>
                  </MenuItem>
                  <MenuItem value="transfer">
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <SwapHorizIcon sx={{ mr: 1, color: 'info.main' }} />
                      Transfer Inventory (Move to another location)
                    </Box>
                  </MenuItem>
                  <MenuItem value="ignore">
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <CancelIcon sx={{ mr: 1, color: 'warning.main' }} />
                      Ignore Discrepancy (No changes to inventory)
                    </Box>
                  </MenuItem>
                </Select>
              </FormControl>

              {resolutionType === 'transfer' && (
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>Transfer Location</InputLabel>
                  <Select
                    value={transferLocationId}
                    onChange={(e) => setTransferLocationId(e.target.value)}
                    label="Transfer Location"
                  >
                    {locations.map((location) => (
                      <MenuItem
                        key={location.id}
                        value={location.id}
                        disabled={location.id === selectedDiscrepancy.location_id}
                      >
                        {`${location.rack}-${location.shelf}`}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}

              <TextField
                label="Resolution Notes"
                multiline
                rows={3}
                value={resolutionNotes}
                onChange={(e) => setResolutionNotes(e.target.value)}
                fullWidth
                required
                placeholder="Explain why this discrepancy occurred and how it's being resolved"
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            onClick={handleResolveDiscrepancy}
            color="primary"
            variant="contained"
            disabled={resolving || !resolutionNotes || (resolutionType === 'transfer' && !transferLocationId)}
          >
            {resolving ? <CircularProgress size={24} /> : 'Resolve Discrepancy'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default PhysicalInventoryReport;
